// De lange hændelser fra bane 5 og 6: vindstille, kabelskade og værksnedbrud.
// De kan ikke jages væk; figuren fortæller bare, hvad der sker og hvor længe. Vindstillen lægger et
// gråt slør over kortet, kabelskaden lyser orange langs kablet, og værket, der falder ud, ryger.

import type { BitmapText, Graphics, Sprite } from 'pixi.js';
import type { Threat, ThreatKind } from '../sim';
import { TICK_HZ } from '../sim';
import { bez } from './geo';
import type { P } from './geo';
import { C } from './palette';
import type { View } from './view';

const OUT = C.ink;

/** Lukkede, søvnige øjne. */
function sleepy(g: Graphics, x: number, y: number, dx: number): void {
  for (const sx of [x - dx, x + dx]) g.moveTo(sx - 2.4, y).quadraticCurveTo(sx, y + 2, sx + 2.4, y);
  g.stroke({ width: 1.5, color: OUT, cap: 'round' });
}

/** Vindstille: en grå, søvnig sky med et lille z. */
function fog(g: Graphics): void {
  g.ellipse(0, 0, 18, 3).fill({ color: OUT, alpha: 0.12 });
  g.circle(-9, -14, 8).circle(0, -19, 10).circle(10, -14, 8).fill(C.cloudLight).stroke({ width: 2, color: OUT });
  g.roundRect(-17, -16, 34, 10, 5).fill(C.cloudLight);
  g.moveTo(-17, -7).lineTo(18, -7).stroke({ width: 2, color: OUT, cap: 'round' });
  sleepy(g, 0, -15, 4);
  g.moveTo(-2, -10).quadraticCurveTo(0, -9, 2, -10).stroke({ width: 1.3, color: OUT, cap: 'round' });
  // Et lille z over skyen.
  g.moveTo(12, -34).lineTo(18, -34).lineTo(12, -28).lineTo(18, -28).stroke({ width: 1.8, color: C.petrol, cap: 'round', join: 'round' });
}

/** Kabelskade: et kabelstykke med et orange knæk og et bekymret ansigt. */
function crack(g: Graphics): void {
  g.ellipse(0, 0, 18, 3).fill({ color: OUT, alpha: 0.15 });
  g.roundRect(-20, -18, 17, 10, 4).fill(C.petrol).stroke({ width: 2, color: OUT });
  g.roundRect(3, -18, 17, 10, 4).fill(C.petrol).stroke({ width: 2, color: OUT });
  g.moveTo(-3, -22).lineTo(1, -15).lineTo(-2, -12).lineTo(3, -4).stroke({ width: 2.4, color: C.orange, cap: 'round', join: 'round' });
  for (const sx of [-12, 12]) {
    g.circle(sx - 2.4, -14, 1.3).fill(C.white);
    g.circle(sx + 2.4, -14, 1.3).fill(C.white);
  }
  g.moveTo(-14, -10).quadraticCurveTo(-12, -11.5, -10, -10).moveTo(10, -10).quadraticCurveTo(12, -11.5, 14, -10);
  g.stroke({ width: 1.2, color: C.white, cap: 'round' });
}

/** Værksnedbrud: en skorsten med en grå røgsky og svimle øjne. */
function smoke(g: Graphics): void {
  g.ellipse(0, 0, 16, 3).fill({ color: OUT, alpha: 0.15 });
  g.roundRect(-9, -26, 12, 26, 2).fill(C.facade).stroke({ width: 2, color: OUT });
  g.rect(-9, -22, 12, 3).fill(C.orange);
  g.circle(4, -34, 6).circle(11, -38, 7).circle(-2, -38, 5).fill(C.smoke).stroke({ width: 1.6, color: OUT });
  // Svimle øjne: små krydser.
  for (const sx of [-6, 0]) g.moveTo(sx - 1.6, -14.6).lineTo(sx + 1.6, -11.4).moveTo(sx + 1.6, -14.6).lineTo(sx - 1.6, -11.4);
  g.stroke({ width: 1.4, color: OUT, cap: 'round' });
  g.circle(-3, -7, 1.6).stroke({ width: 1.3, color: OUT });
}

/** Teksturerne til atlasset. */
export const M5_DRAWS: Record<string, (g: Graphics) => void> = { fog, crack, smoke };

const TEX: Partial<Record<ThreatKind, string>> = { dunkelflaute: 'fog', cable_half: 'crack', plant_trip: 'smoke' };

export function isLongThreat(kind: ThreatKind): boolean {
  return kind in TEX;
}

export function m5Texture(kind: ThreatKind): string | null {
  return TEX[kind] ?? null;
}

export interface M5Parts {
  body: Sprite;
  marker: Sprite;
  count: BitmapText;
  at: P;
  /** Spor: telefonkort: forskydningen væk fra huse, navne og andre figurer (declutter.ts). */
  nudge?: P;
}

function anchorOf(t: Threat, v: View): P | null {
  const k = v.k;
  if (t.kind === 'cable_half') {
    const geo = v.lines.get(t.targetLine);
    if (!geo) return null;
    const m = bez(geo.a, geo.c, geo.b, 0.5);
    return { x: m.x, y: m.y - 8 * k };
  }
  if (t.kind === 'plant_trip') {
    const q = v.pos.get(t.nodes?.[0] ?? '');
    return q ? { x: q.x + 22 * k, y: q.y - 36 * k } : null;
  }
  // Vindstillen hænger øverst over kortet.
  const c = v.cam.toScreen({ x: v.bounds.x + v.bounds.w / 2, y: v.bounds.y + v.bounds.h * 0.18 });
  return { x: Math.max(50 * k, Math.min(v.width - 50 * k, c.x)), y: Math.max(70 * k, c.y) };
}

/** Tegn én lang hændelse. Båndet (g) ligger under noderne. */
export function drawLongThreat(t: Threat, tv: M5Parts, v: View, g: Graphics): void {
  const k = v.k;
  const clock = v.clock;
  const a0 = anchorOf(t, v);
  const p = a0 && tv.nudge ? { x: a0.x + tv.nudge.x, y: a0.y + tv.nudge.y } : a0;
  if (!p || t.phase === 'done') {
    tv.body.visible = tv.marker.visible = tv.count.visible = false;
    return;
  }
  const warn = t.phase === 'warning';
  const pulse = v.calm ? 0.5 : 0.5 + 0.5 * Math.sin(v.now / 260);
  const fade = warn ? Math.min(1, (clock - t.warnAt) / 8) : Math.max(0, Math.min(1, (t.endAt - clock) / 20));

  if (t.kind === 'dunkelflaute' && !warn) {
    // Et gråt slør over hele kortet: vind og sol giver næsten intet.
    g.rect(0, 0, v.width, Math.max(v.width, 1200) * 3).fill({ color: C.dusk, alpha: 0.1 * fade });
  } else if (t.kind === 'cable_half') {
    const geo = v.lines.get(t.targetLine);
    if (geo) {
      g.moveTo(geo.a.x, geo.a.y).quadraticCurveTo(geo.c.x, geo.c.y, geo.b.x, geo.b.y);
      g.stroke({ width: 10 * k, color: C.orange, alpha: (warn ? 0.14 + pulse * 0.1 : 0.28 + pulse * 0.12) * fade, cap: 'round' });
    }
  } else if (t.kind === 'plant_trip') {
    const q = v.pos.get(t.nodes?.[0] ?? '');
    if (q) {
      const r = (warn ? 26 + pulse * 6 : 30) * k;
      g.circle(q.x, q.y, r).stroke({ width: 2.5 * k, color: warn ? C.orange : C.dusk, alpha: (warn ? 0.5 + pulse * 0.3 : 0.5) * fade });
    }
  }

  const bob = v.calm ? 0 : Math.sin(v.now / 380 + t.warnAt) * 2 * k;
  tv.body.visible = true;
  tv.body.alpha = fade;
  tv.body.position.set(p.x, p.y + bob);
  tv.body.scale.set(k * 1.05);
  tv.at = { x: p.x, y: p.y - 18 * k };

  // Nedtælling: til nedslaget i varslet, og til det er ovre bagefter.
  const until = warn ? t.hitAt : t.endAt;
  const secs = Math.max(0, Math.ceil((until - clock) / TICK_HZ));
  const my = Math.max(20 * k, p.y - 56 * k);
  tv.marker.visible = warn;
  tv.count.visible = secs > 0;
  tv.marker.position.set(p.x - 14 * k, my);
  tv.marker.scale.set(k * 0.7);
  tv.count.text = `${secs}s`;
  tv.count.position.set(warn ? p.x + 16 * k : p.x, my + 2 * k);
  tv.count.scale.set(k * 0.7);
  tv.count.alpha = warn ? 1 : 0.75;
}
